// src/hooks/useGeneticData.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import {
	getAllPanels,
	getPanelsForSample,
	getPanelById,
	getTraitsForPanel,
	getAllSnpsForPanel
} from '../../services/wheelApiService';
import { getSamplesGeneStages } from '../../api/geneCodes/GeneStageApi';
import { PanelStatusFilter } from '../../api/geneCodes/PanelApi';
import { Panel, PanelTrait } from '../../types/geneCodes/Panel';
import { Staging } from '../../types/geneCodes/StagingTypes';
import { GeneStage } from '../../types/geneCodes/GeneStageTypes';
import { getAiPanelDataForSample } from '../../api/geneCodes/AiPanelDataApi';
import { AiPanelData } from '../../types/geneCodes/AiPanelDataTypes';
import type { PatientSex } from '../../utils/geneCodes/referenceRange';

interface UseGeneticDataProps {
	initialPanelId?: number;
	sampleId?: string;
	// Builder-only. Omitted, the server returns published wheels alone — which is what WheelModal,
	// the patient-facing caller of this same hook, must keep getting.
	panelStatus?: PanelStatusFilter;
	// The rail lists only what labiq releases for this sample; the server, not this flag, decides staff vs patient.
	scopeToSample?: boolean;
	// Relevance filter for a patient's scoped rail; ignored unless scopeToSample.
	sex?: PatientSex;
	// Builder-only: asks labiq for hidden traits, genes and SNPs too. Never set by a viewer surface.
	includeHidden?: boolean;
}

interface UseGeneticDataReturn {
	panels: Panel[];
	selectedPanel: Panel | null;
	traits: PanelTrait[];
	snps: Staging[];
	geneticData: GeneStage[];
	loading: {
		panels: boolean;
		selectedPanel: boolean;
		traits: boolean;
		snps: boolean;
		geneticData: boolean;
	};
	error: {
		panels: string | null;
		selectedPanel: string | null;
		traits: string | null;
		snps: string | null;
		geneticData: string | null;
	};
	selectPanel: (panelId: number, sampleId: string | undefined) => void;
	refreshPanels: () => Promise<void>;
	clearSelectedPanel: () => void;
	aiPanelData: AiPanelData | null;
}

/**
 * Custom hook to manage genetic data for the wheel visualization
 */
export const useGeneticData = ({ initialPanelId, sampleId, panelStatus, scopeToSample = false, sex, includeHidden = false }: UseGeneticDataProps = {}): UseGeneticDataReturn => {
	// State for data
	const [panels, setPanels] = useState<Panel[]>([]);
	const [selectedPanel, setSelectedPanel] = useState<Panel | null>(null);
	const [traits, setTraits] = useState<PanelTrait[]>([]);
	const [snps, setSnps] = useState<Staging[]>([]);
	// State for genetic data
	const [geneticData, setGeneticData] = useState<GeneStage[]>([]);
	const [aiPanelData, setAiPanelData] = useState<AiPanelData | null>(null);

	// Loading states
	const [loading, setLoading] = useState({
		panels: true,
		selectedPanel: false,
		traits: false,
		snps: false,
		geneticData: false,
	});

	// Error states
	const [error, setError] = useState({
		panels: null as string | null,
		selectedPanel: null as string | null,
		traits: null as string | null,
		snps: null as string | null,
		geneticData: null as string | null,
	});

	// Unscoped callers must not refetch the rail when their sample changes, so these stay undefined for them.
	const scopedSampleId = scopeToSample ? sampleId : undefined;
	const scopedSex = scopeToSample ? sex : undefined;
	// A sample switch can land an older request last; only the newest may write the rail.
	const panelRequest = useRef(0);

	// The rail's list. Exposed as well as run on mount so the builder can re-read it after a panel
	// is created, renamed or deleted.
	const refreshPanels = useCallback(async () => {
		const request = ++panelRequest.current;

		if (scopeToSample && !scopedSampleId) {
			setPanels([]);
			setError(prev => ({ ...prev, panels: null }));
			setLoading(prev => ({ ...prev, panels: false }));
			return;
		}

		try {
			setLoading(prev => ({ ...prev, panels: true }));
			const data = scopedSampleId
				? await getPanelsForSample(scopedSampleId, scopedSex)
				// A wheel with no SNPs draws nothing, so the viewer rail hides it; build mode must still list it so an author can fill it.
				: await getAllPanels(undefined, undefined, undefined, panelStatus, panelStatus === undefined);
			if (request !== panelRequest.current) return;
			setPanels(data);
			setError(prev => ({ ...prev, panels: null }));
		} catch (err) {
			if (request !== panelRequest.current) return;
			const message = err instanceof Error ? err.message : 'Unknown error fetching panels';
			setError(prev => ({ ...prev, panels: message }));
		} finally {
			if (request === panelRequest.current) setLoading(prev => ({ ...prev, panels: false }));
		}
	}, [panelStatus, scopeToSample, scopedSampleId, scopedSex]);

	// Fetch all panels on component mount
	useEffect(() => {
		refreshPanels();
	}, [refreshPanels]);

	// A deleted panel has nothing to re-select, and selectPanel is the only other writer of this state.
	const clearSelectedPanel = useCallback(() => {
		setSelectedPanel(null);
		setTraits([]);
		setSnps([]);
		setAiPanelData(null);
	}, []);

	// Fetch genetic data once if sampleId is provided
	useEffect(() => {
		const fetchGeneticData = async () => {
			if (!sampleId) {
				setGeneticData([]);
				return;
			}

			try {
				setLoading(prev => ({ ...prev, geneticData: true }));
				setError(prev => ({ ...prev, geneticData: null }));

				const geneStages = await getSamplesGeneStages(sampleId);
				setGeneticData(geneStages || []);
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Unknown error fetching genetic data';
				setError(prev => ({ ...prev, geneticData: message }));
				setGeneticData([]); // Set empty array on error
			} finally {
				setLoading(prev => ({ ...prev, geneticData: false }));
			}
		};

		fetchGeneticData();
	}, [sampleId]); // Only depend on sampleId

	// Fetch initial panel if provided
	useEffect(() => {
		if (initialPanelId && panels.length > 0) {
			selectPanel(initialPanelId, undefined);
		}
	}, [initialPanelId, panels]);

	// Function to select a panel and fetch its data
	const selectPanel = useCallback(async (panelId: number, sampleId: string | undefined) => {
		try {
			// Clear previous data
			setTraits([]);
			setSnps([]);
			// The AI-data endpoint returns 200 null for an ungenerated panel, so a stale report survives without this.
			setAiPanelData(null);

			// Set loading states
			setLoading(prev => ({
				...prev,
				selectedPanel: true,
				traits: true,
				snps: true,
			}));

			// Reset errors
			setError(prev => ({
				...prev,
				selectedPanel: null,
				traits: null,
				snps: null,
			}));

			// Fetch panel details
			const panel = await getPanelById(panelId, includeHidden);
			setSelectedPanel(panel);

			// Fetch traits for the panel
			const traitsData = await getTraitsForPanel(panelId, undefined, includeHidden);
			setTraits(traitsData);

			// Fetch SNPs for the panel
			const snpsData = await getAllSnpsForPanel(panel.name_panel, includeHidden);
			setSnps(snpsData);

			// Own try/catch: an AI-data failure must not abort selectPanel and leave traits/snps unset.
			if (sampleId) {
				try {
					const aiData = await getAiPanelDataForSample(sampleId, panelId);
					setAiPanelData(aiData);
				} catch (aiErr) {
					setAiPanelData(null);
					console.error('gene_codes.ai_panel_data.fetch.failed', { panel_id: panelId, err: aiErr });
				}
			}

		} catch (err) {
			const message = err instanceof Error ? err.message : 'Unknown error';
			setError(prev => ({
				...prev,
				selectedPanel: message,
				traits: 'Failed to load traits',
				snps: 'Failed to load SNPs',
			}));
		} finally {
			setLoading(prev => ({
				...prev,
				selectedPanel: false,
				traits: false,
				snps: false,
			}));
		}
	}, [includeHidden]);


	return {
		panels,
		selectedPanel,
		traits,
		snps,
		geneticData,
		loading,
		error,
		selectPanel,
		refreshPanels,
		clearSelectedPanel,
		aiPanelData
	};
};